import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faCheckCircle, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MarkPaidBulkItemResult } from '../../models';

export interface MarkPaidBulkResultModalRef {
  open: (results: MarkPaidBulkItemResult[]) => void;
  close: () => void;
}

export const MarkPaidBulkResultModal = forwardRef<MarkPaidBulkResultModalRef>((_, ref) => {
  const { t } = useTranslation('withdrawalsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [results, setResults] = useState<MarkPaidBulkItemResult[]>([]);

  useImperativeHandle(ref, () => ({
    open: (bulkResults) => {
      setResults(bulkResults);
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;

  return (
    <ModalCenter ref={modalRef} title={t('modals.markPaidBulkResult.title')} width="lg">
      <div className="space-y-4">
        <p className="text-sm text-subtext">{t('modals.markPaidBulkResult.description')}</p>

        <div className="flex flex-wrap gap-2">
          <Badge variant="transparent-green">
            {t('modals.markPaidBulkResult.successCount', { count: successCount })}
          </Badge>
          {errorCount > 0 && (
            <Badge variant="destructive">{t('modals.markPaidBulkResult.errorCount', { count: errorCount })}</Badge>
          )}
        </div>

        {/* Resultados individuales - Individual results */}
        <div className="max-h-72 overflow-y-auto space-y-2">
          {results.map((result) => (
            <div
              key={String(result.id)}
              className="flex items-start gap-3 rounded-lg border border-lines p-3"
              data-testid={`mark-paid-bulk-result-${result.id}`}
            >
              <FontAwesomeIcon
                icon={result.status === 'success' ? faCheckCircle : faCircleXmark}
                className={`w-4 h-4 mt-0.5 ${result.status === 'success' ? 'text-success' : 'text-error'}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-title">#{result.id}</p>
                {result.status === 'error' && result.reason && (
                  <p className="text-xs text-error mt-1">{result.reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            variant="primary"
            onClick={() => modalRef.current?.closeModal()}
            data-testid="close-mark-paid-bulk-result"
          >
            {t('buttons.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

MarkPaidBulkResultModal.displayName = 'MarkPaidBulkResultModal';
